import { useSubscription } from '@/context/SubscriptionContext';
import { toast } from '@/hooks/ui/use-toast';

/**
 * Custom hook for feature gating based on subscription plans
 * Provides utilities to check feature access and handle upgrade prompts
 */
export const useFeatureGating = () => {
  const { checkFeature, checkLimit, currentPlan, subscriptionSummary } = useSubscription();

  /**
   * Check if a premium feature is available in the current plan
   * @param featureName - The feature to check (e.g., 'payroll', 'advanced_analytics')
   * @returns boolean - Whether the feature is available
   */
  const hasFeature = (featureName: string): boolean => {
    return checkFeature(featureName);
  };

  /**
   * Check if user can perform an action within subscription limits
   * @param feature - The feature type ('businesses', 'employees', 'shifts', etc.)
   * @param businessId - Optional business ID for per-business limits
   * @param increment - Number to increment by (default 1)
   * @returns Promise<boolean> - Whether the action is allowed
   */
  const canPerformAction = async (
    feature: string, 
    businessId?: string, 
    increment: number = 1
  ): Promise<boolean> => {
    return await checkLimit(feature, businessId, increment);
  };

  /**
   * Show upgrade prompt with relevant messaging
   * @param featureName - The feature that requires an upgrade
   * @param customMessage - Optional custom message
   */
  const showUpgradePrompt = (featureName: string, customMessage?: string) => {
    const featureDisplayNames: Record<string, string> = {
      payroll: 'Payroll Management',
      advanced_analytics: 'Advanced Analytics',
      whatsapp_integration: 'WhatsApp Integration',
      api_access: 'API Access',
      custom_branding: 'Custom Branding',
      priority_support: 'Priority Support',
      sso: 'Single Sign-On',
      audit_logs: 'Audit Logs',
      advanced_reporting: 'Advanced Reporting',
      bulk_operations: 'Bulk Operations',
    };

    const displayName = featureDisplayNames[featureName] || featureName;
    const message = customMessage || `${displayName} is available in higher tier plans.`;

    toast.error('Premium Feature Required', {
      description: message,
      action: {
        label: 'Upgrade Plan',
        onClick: () => {
          // Navigate to subscription management or show upgrade modal
          window.location.hash = '#subscription';
        },
      },
    });
  };

  /**
   * Show limit reached prompt with current usage info
   * @param limitType - The type of limit reached
   * @param businessId - Optional business ID for context
   */
  const showLimitPrompt = (limitType: string, businessId?: string) => {
    const limitDisplayNames: Record<string, string> = {
      businesses: 'businesses',
      employees: 'employees',
      shifts: 'shifts',
      loyalty_programs: 'loyalty programs',
      storage: 'storage space',
      monthly_emails: 'monthly emails',
      api_calls: 'API calls',
    };

    const displayName = limitDisplayNames[limitType] || limitType;
    const currentUsage = getCurrentUsage(limitType);
    const limit = getCurrentLimit(limitType);

    toast.error(`${displayName.charAt(0).toUpperCase() + displayName.slice(1)} Limit Reached`, {
      description: `You've reached your limit of ${limit} ${displayName}. Upgrade to increase your limits.`,
      action: {
        label: 'Upgrade Plan',
        onClick: () => {
          window.location.hash = '#subscription';
        },
      },
    });
  };

  /**
   * Get current usage for a specific metric
   * @param metric - The metric to get usage for
   * @returns number - Current usage count
   */
  const getCurrentUsage = (metric: string): number => {
    if (!subscriptionSummary) return 0;
    
    switch (metric) {
      case 'businesses':
        return subscriptionSummary.businesses_used;
      case 'employees':
        return subscriptionSummary.employees_used;
      case 'shifts':
        return subscriptionSummary.shifts_used;
      case 'storage':
        return subscriptionSummary.storage_used_mb;
      default:
        return 0;
    }
  };

  /**
   * Get current limit for a specific metric
   * @param metric - The metric to get limit for
   * @returns number | null - Current limit (null = unlimited)
   */
  const getCurrentLimit = (metric: string): number | null => {
    if (!subscriptionSummary) return null;
    
    switch (metric) {
      case 'businesses':
        return subscriptionSummary.businesses_limit;
      case 'employees':
        return subscriptionSummary.employees_limit;
      case 'shifts':
        return subscriptionSummary.shifts_limit;
      case 'storage':
        return subscriptionSummary.storage_limit_mb;
      default:
        return null;
    }
  };

  /**
   * Get usage percentage for a specific metric
   * @param metric - The metric to calculate percentage for
   * @returns number - Usage percentage (0-100), or 0 if unlimited
   */
  const getUsagePercentage = (metric: string): number => {
    const usage = getCurrentUsage(metric);
    const limit = getCurrentLimit(metric);
    
    if (limit === null || limit === 0) return 0; // Unlimited or no limit set
    return Math.round((usage / limit) * 100);
  };

  /**
   * Check if usage is approaching the limit (>80%)
   * @param metric - The metric to check
   * @returns boolean - Whether usage is approaching limit
   */
  const isApproachingLimit = (metric: string): boolean => {
    const percentage = getUsagePercentage(metric);
    return percentage >= 80;
  };

  /**
   * Check if limit has been reached (100%)
   * @param metric - The metric to check
   * @returns boolean - Whether limit has been reached
   */
  const isLimitReached = (metric: string): boolean => {
    const percentage = getUsagePercentage(metric);
    return percentage >= 100;
  };

  /**
   * Get plan display name
   * @returns string - Current plan display name
   */
  const getPlanDisplayName = (): string => {
    return currentPlan?.display_name || 'Free Plan';
  };

  /**
   * Check if user is on free plan
   * @returns boolean - Whether user is on free plan
   */
  const isFreePlan = (): boolean => {
    return currentPlan?.name === 'free' || !currentPlan;
  };

  /**
   * Check if user is on enterprise plan
   * @returns boolean - Whether user is on enterprise plan
   */
  const isEnterprisePlan = (): boolean => {
    return currentPlan?.name === 'enterprise';
  };

  return {
    // Feature checks
    hasFeature,
    canPerformAction,
    
    // Prompts and messaging
    showUpgradePrompt,
    showLimitPrompt,
    
    // Usage metrics
    getCurrentUsage,
    getCurrentLimit,
    getUsagePercentage,
    isApproachingLimit,
    isLimitReached,
    
    // Plan information
    getPlanDisplayName,
    isFreePlan,
    isEnterprisePlan,
    
    // Raw data access
    currentPlan,
    subscriptionSummary,
  };
};